import React, { useRef, useState } from 'react';
import { ArrowLeft, Check, Eye, EyeOff, KeyRound, LockKeyhole, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { authApi } from '../../api/authApi';

const getErr = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const inputClass = 'box-border w-full   rounded-lg border-0 bg-[#06110c] px-3.5 py-3 text-sm font-medium text-white outline-none ring-1 ring-inset ring-white/10 transition placeholder:text-white/25 hover:ring-white/20 focus:ring-2 focus:ring-[#20c77a]';
const primaryButton = 'inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-[#20c77a] bg-[#12ad68] px-5 text-sm font-bold text-[#02140b] shadow-[0_10px_30px_rgba(18,173,104,0.16)] transition hover:-translate-y-0.5 hover:bg-[#25d080] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0';

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');

  const inputsRef = useRef([]);
  const otpString = otp.join('');

  const requestCode = async (event) => {
    event?.preventDefault();
    if (!email.trim()) return;
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setOtp(['', '', '', '']);
      setStep(2);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    } catch (err) {
      setError(getErr(err, 'No account found for that email.'));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError('');
    setInfo('');
    setResending(true);
    try {
      await authApi.forgotPassword(email.trim());
      setInfo('A new verification code has been sent to your email and mobile.');
    } catch (err) {
      setError(getErr(err, 'Could not resend the code. Please try again.'));
    } finally {
      setResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 3) inputsRef.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) inputsRef.current[index - 1]?.focus();
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    const next = ['', '', '', ''];
    pasted.split('').forEach((digit, index) => { next[index] = digit; });
    setOtp(next);
    inputsRef.current[Math.min(pasted.length, 4) - 1]?.focus();
  };

  const verifyCode = async (event) => {
    event?.preventDefault();
    if (otpString.length !== 4) return setError('Please enter the full 4-digit code.');
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await authApi.verifyResetOtp(email.trim(), otpString);
      setStep(3);
    } catch (err) {
      setError(getErr(err, 'Invalid code. Please check and try again.'));
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (event) => {
    event.preventDefault();
    setError('');
    if (form.password.length < 8) return setError('Password must be at least 8 characters.');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      await authApi.resetPassword({ email: email.trim(), otp: otpString, newPassword: form.password, confirmPassword: form.confirmPassword });
      setStep(4);
    } catch (err) {
      setError(getErr(err, 'Could not reset password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-screen w-full items-center justify-center overflow-x-hidden bg-[#020906] px-4 py-8 font-dm text-white sm:px-6 sm:py-10">
      <title>Reset Password | GIGGS</title>
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(25,177,105,0.14),transparent_34%),linear-gradient(rgba(255,255,255,0.018)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.018)_1px,transparent_1px)] bg-[size:auto,22px_22px,22px_22px]" />

      <section className="relative z-10 w-full max-w-[560px] rounded-2xl border border-[#1c4b36]/70 bg-[#04100b]/95 p-5 shadow-[0_25px_90px_rgba(0,0,0,0.38)] backdrop-blur-sm sm:p-7 lg:p-8">
        <div className="flex items-center justify-between gap-4">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs font-semibold text-white/40 no-underline transition hover:text-[#46dc96] sm:text-sm"><ArrowLeft size={16} /> Back to sign in</Link>
          <Link to="/" aria-label="Giggs home"><img src="/images/logo-white.png" alt="Giggs" className="h-8 w-auto" /></Link>
        </div>

        {step < 4 && (
          <div className="mx-auto mt-7 flex w-full max-w-xs items-center justify-center">
            {[1, 2, 3].map((number) => (
              <React.Fragment key={number}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${step >= number ? 'border-[#20c77a] bg-[#0d6d43] text-[#7dffba]' : 'border-white/10 bg-[#06110c] text-white/25'}`}>{step > number ? <Check size={13} strokeWidth={3} /> : number}</span>
                {number < 3 && <span className={`h-px min-w-0 flex-1 ${step > number ? 'bg-[#20c77a]/60' : 'bg-white/10'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {error && <div className="mt-5 rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-3 text-center text-sm font-medium text-red-200">{error}</div>}
        {info && !error && <div className="mt-5 rounded-lg border border-[#20c77a]/20 bg-[#20c77a]/10 px-4 py-3 text-center text-sm font-medium text-[#79efb7]">{info}</div>}

        {step === 1 && (
          <div className="mt-7 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#20c77a]/20 bg-[#0b2418] text-[#4de498]"><LockKeyhole size={22} /></span>
            <h1 className="mb-0 mt-4 font-nunito text-2xl font-bold text-white">Forgot your password?</h1>
            <p className="mx-auto mb-0 mt-2 max-w-md text-sm leading-6 text-white/35">Enter your account email and we'll send a 4-digit verification code to your email and mobile.</p>
            <form onSubmit={requestCode} className="mt-6 text-left">
              <label className="block"><span className="mb-1.5 block text-[11px] font-semibold text-white/65">Email address</span><div className="relative"><Mail size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25" /><input className={`${inputClass} pl-10`} type="email" placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></div></label>
              <button type="submit" disabled={loading} className={`${primaryButton} mt-5`}>{loading ? 'Sending code…' : 'Send code'}</button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="mt-7 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#20c77a]/20 bg-[#0b2418] text-[#4de498]"><KeyRound size={22} /></span>
            <h1 className="mb-0 mt-4 font-nunito text-2xl font-bold text-white">Enter your verification code</h1>
            <p className="mb-0 mt-2 break-words text-sm leading-6 text-white/35">We sent a 4-digit code to <strong className="text-white/70">{email}</strong>.</p>
            <form onSubmit={verifyCode} className="mt-6">
              <div className="mx-auto grid w-full max-w-xs grid-cols-4 gap-2 sm:gap-3">
                {otp.map((digit, index) => (
                  <input key={index} ref={(element) => { inputsRef.current[index] = element; }} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={(event) => handleOtpChange(index, event.target.value)} onKeyDown={(event) => handleOtpKeyDown(index, event)} onPaste={handleOtpPaste} aria-label={`Verification digit ${index + 1}`} className={`aspect-square min-w-0 w-full rounded-lg border-0 bg-[#06110c] text-center text-xl font-bold text-white outline-none ring-1 ring-inset transition sm:text-2xl ${digit ? 'ring-2 ring-[#20c77a]' : 'ring-white/10 focus:ring-2 focus:ring-[#20c77a]'}`} />
                ))}
              </div>
              <button type="button" onClick={resendCode} disabled={resending} className="mx-auto mt-4 block border-0 bg-transparent p-1 text-xs font-semibold text-[#42d993] disabled:opacity-50">{resending ? 'Sending…' : "Didn't get it? Resend code"}</button>
              <button type="submit" disabled={loading || otpString.length !== 4} className={`${primaryButton} mt-4`}>{loading ? 'Checking code…' : 'Verify code'}</button>
            </form>
            <button type="button" onClick={() => { setStep(1); setError(''); setInfo(''); }} className="mx-auto mt-4 block border-0 bg-transparent p-1 text-xs font-semibold text-white/35 transition hover:text-white/65">Use a different email</button>
          </div>
        )}

        {step === 3 && (
          <div className="mt-7 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#20c77a]/20 bg-[#0b2418] text-[#4de498]"><LockKeyhole size={22} /></span>
            <h1 className="mb-0 mt-4 font-nunito text-2xl font-bold text-white">Create a new password</h1>
            <p className="mb-0 mt-2 text-sm leading-6 text-white/35">Use at least 8 characters for your new password.</p>
            <form onSubmit={submitNewPassword} className="mt-6 space-y-4 text-left">
              <label className="block"><span className="mb-1.5 block text-[11px] font-semibold text-white/65">New password</span><div className="relative"><input className={`${inputClass} pr-11`} type={showPassword ? 'text' : 'password'} value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} placeholder="Minimum 8 characters" autoComplete="new-password" minLength={8} required /><button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md border-0 bg-transparent text-white/25 transition hover:bg-white/[0.05] hover:text-white/70">{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button></div></label>
              <label className="block"><span className="mb-1.5 block text-[11px] font-semibold text-white/65">Confirm new password</span><input className={inputClass} type={showPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={(event) => setForm((current) => ({ ...current, confirmPassword: event.target.value }))} placeholder="Re-enter password" autoComplete="new-password" minLength={8} required /></label>
              <button type="submit" disabled={loading} className={primaryButton}>{loading ? 'Resetting password…' : 'Reset password'}</button>
            </form>
          </div>
        )}

        {step === 4 && (
          <div className="mt-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#20c77a]/30 bg-[#0d6d43] text-[#7dffba]"><Check size={27} strokeWidth={3} /></span>
            <h1 className="mb-0 mt-5 font-nunito text-2xl font-bold text-white">Password reset</h1>
            <p className="mb-0 mt-2 text-sm leading-6 text-white/35">Your password has been successfully updated.</p>
            <Link to="/login" className={`${primaryButton} mt-6 no-underline`}>Sign in now</Link>
          </div>
        )}
      </section>
    </main>
  );
};

export default ForgotPassword;
